import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Icon, type IconName } from "../components/Icon.tsx";
import { Shell } from "../components/Shell.tsx";
import {
  Button,
  Confirm,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Skeleton,
  StatusBadge,
  Switch,
  type Tone,
} from "../components/ui.tsx";
import { t, tn, type MessageKey } from "../i18n/index.ts";
import { action, subscribe } from "../lib/api.ts";
import { clock, dateTime, day as dayText, todayIso } from "../lib/format.ts";
import { comingFor, refreshVisitors } from "../lib/frontdesk.ts";
import { usePlaces } from "../lib/places.tsx";
import { useSession } from "../lib/session.tsx";
import { useUi } from "../lib/ui-state.tsx";
import { useAction, useSave } from "./manage/common.tsx";
import { downloadCsv, Kpi, Table } from "./Reports.tsx";

/**
 * Frontdesk: visitors sign themselves in on a tablet at the entrance and the person they visit
 * hears at once. Reception sees who is in the building, who is expected and which drinks to
 * bring; everyone can tell reception about a visitor they expect.
 */

type VState = "planned" | "in" | "out" | "cancelled";
type Tab = "board" | "mine" | "register" | "setup";

interface Visit {
  id: string;
  name: string;
  company: string | null;
  phone: string | null;
  host: string | null;
  host_name?: string | null;
  purpose: string | null;
  drink: string | null;
  drink_name: string | null;
  drink_served: boolean;
  state: VState;
  planned_at: string | null;
  checked_in_at: string | null;
  checked_out_at: string | null;
  badge: number | null;
  host_seen_at: string | null;
  location: string;
  location_name: string;
  note: string | null;
}
interface Board {
  today: string;
  inside: Visit[];
  expected: Visit[];
  left: Visit[];
  drinks: Visit[];
  overnight: number;
  counts: { inside: number; expected: number; today: number };
}
interface Station {
  id: string;
  name: string;
  location: string;
  location_name: string;
  active: boolean;
  ask_company: boolean;
  ask_phone: boolean;
  ask_host: boolean;
  offer_drinks: boolean;
  welcome: string | null;
}
interface Drink {
  id: string;
  name: string;
  active: boolean;
}
interface Setup {
  stations: Station[];
  drinks: Drink[];
  keep_days: number;
}
interface Person {
  id: string;
  name: string;
}

const V_STATE: Record<VState, { tone: Tone; icon: IconName }> = {
  planned: { tone: "info", icon: "calendar" },
  in: { tone: "ok", icon: "contact" },
  out: { tone: "neutral", icon: "logout" },
  cancelled: { tone: "neutral", icon: "x" },
};
const stateBadge = (s: VState) => (
  <StatusBadge
    tone={V_STATE[s].tone}
    icon={V_STATE[s].icon}
    label={t(`fd.state_${s}` as MessageKey)}
  />
);
const who = (v: { name: string; company: string | null }) =>
  v.company ? `${v.name} · ${v.company}` : v.name;

/** Reloads when anything changes at reception. */
function useLive(reload: () => unknown) {
  const again = useRef(reload);
  again.current = reload;
  useEffect(
    () =>
      subscribe(["frontdesk"], (e) => e !== "ready" && void again.current()),
    [],
  );
}

export function Frontdesk() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const tabs: { key: Tab; icon: IconName; show: boolean }[] = [
    { key: "board", icon: "contact", show: can("frontdesk.view") },
    { key: "mine", icon: "calendar", show: true },
    { key: "register", icon: "book", show: can("frontdesk.view") },
    { key: "setup", icon: "settings", show: can("frontdesk.manage") },
  ];
  const visible = tabs.filter((x) => x.show);
  const first = visible[0]!.key;
  const tab = (
    visible.some((x) => x.key === params.get("tab")) ? params.get("tab") : first
  ) as Tab;
  const setTab = (k: Tab) => {
    const p = new URLSearchParams(params);
    if (k === first) p.delete("tab");
    else p.set("tab", k);
    setParams(p, { replace: true });
  };
  return (
    <Shell>
      <div className="page reports fd-page">
        <div className="page-head">
          <div>
            <h1>{t("fd.title")}</h1>
            <p className="muted">
              {t(can("frontdesk.view") ? "fd.subtitle_desk" : "fd.subtitle_me")}
            </p>
          </div>
        </div>
        {visible.length > 1 ? (
          <div
            className="segmented tabs"
            role="tablist"
            aria-label={t("fd.sections")}
          >
            {visible.map((x) => (
              <button
                key={x.key}
                role="tab"
                aria-selected={tab === x.key}
                className={tab === x.key ? "active" : ""}
                onClick={() => setTab(x.key)}
              >
                <Icon name={x.icon} size={16} />
                {t(`fd.tab_${x.key}` as MessageKey)}
              </button>
            ))}
          </div>
        ) : null}
        {tab === "board" ? (
          <BoardTab />
        ) : tab === "mine" ? (
          <MineTab />
        ) : tab === "register" ? (
          <RegisterTab />
        ) : (
          <SetupTab />
        )}
      </div>
    </Shell>
  );
}

// ---- reception ---------------------------------------------------------------------------

function BoardTab() {
  const places = usePlaces();
  const [where, setWhere] = useState<string>("");
  const location = places.multi ? where || null : (places.here?.id ?? null);
  const { data: b, reload } = useAction<Board>("frontdesk.board", { location });
  useLive(reload);
  const { busy, run } = useSave();
  const [plan, setPlan] = useState<Visit | "new" | null>(null);
  const [drop, setDrop] = useState<Visit | null>(null);
  const [closing, setClosing] = useState(false);
  const act = (name: string, input: Record<string, unknown>, done: string) =>
    run(
      async () => (
        await action(name, input),
        await reload(),
        refreshVisitors()
      ),
      done,
    );
  if (!b) return <Skeleton h={300} r="var(--radius-lg)" />;
  const closeAt = location ?? places.here?.id ?? null;
  return (
    <>
      <div className="fd-toolbar">
        {places.multi ? (
          <label className="fd-where">
            <span className="sr-only">{t("fd.location")}</span>
            <select
              className="input"
              value={where}
              onChange={(e) => setWhere(e.target.value)}
            >
              <option value="">{t("fd.all_locations")}</option>
              {places.places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <span className="fd-spacer" />
        <Button icon="plus" onClick={() => setPlan("new")}>
          {t("fd.expect")}
        </Button>
        {b.inside.length && closeAt ? (
          <Button icon="logout" onClick={() => setClosing(true)}>
            {t("fd.sign_out_all")}
          </Button>
        ) : null}
      </div>
      <div className="rep-kpis fd-kpis" aria-label={t("fd.glance")}>
        <Kpi label={t("fd.kpi_inside")} value={String(b.counts.inside)} />
        <Kpi label={t("fd.kpi_expected")} value={String(b.counts.expected)} />
        <Kpi label={t("fd.kpi_today")} value={String(b.counts.today)} />
      </div>
      {b.overnight ? (
        <p className="callout fd-warn" role="status">
          <Icon name="alert" size={16} /> {tn("fd.overnight", b.overnight)}
        </p>
      ) : null}
      <div className="fd-grid">
        <section className="panel fd-panel">
          <div className="panel-head">
            <h2>{t("fd.inside")}</h2>
            <span className="muted small">{b.inside.length}</span>
          </div>
          {!b.inside.length ? (
            <EmptyState
              icon="contact"
              title={t("fd.nobody_in")}
              body={t("fd.nobody_in_body")}
            />
          ) : (
            <ul className="fd-list">
              {b.inside.map((v) => (
                <li key={v.id} className="fd-row">
                  <span
                    className="fd-badge"
                    aria-label={t("fd.badge_no", { n: v.badge ?? 0 })}
                  >
                    {v.badge}
                  </span>
                  <span className="fd-row-text">
                    <strong>{who(v)}</strong>
                    <span className="muted small">
                      {[
                        v.host_name
                          ? t("fd.visiting", { name: v.host_name })
                          : t("fd.no_host"),
                        t("fd.since", { time: clock(v.checked_in_at!) }),
                        places.multi ? v.location_name : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  <span className="fd-row-end">
                    {v.host ? (
                      v.host_seen_at ? (
                        <StatusBadge
                          tone="ok"
                          icon="check"
                          label={t("fd.host_coming")}
                        />
                      ) : (
                        <StatusBadge
                          tone="warn"
                          icon="bell"
                          label={t("fd.host_told")}
                          pulse
                        />
                      )
                    ) : null}
                    <Button
                      size="sm"
                      icon="logout"
                      busy={busy}
                      onClick={() =>
                        void act(
                          "frontdesk.check_out",
                          { id: v.id },
                          t("fd.signed_out", { name: v.name }),
                        )
                      }
                    >
                      {t("fd.sign_out")}
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <div className="fd-side">
          <section className="panel fd-panel">
            <div className="panel-head">
              <h2>{t("fd.drinks")}</h2>
              <span className="muted small">{b.drinks.length}</span>
            </div>
            {!b.drinks.length ? (
              <p className="muted">{t("fd.no_drinks")}</p>
            ) : (
              <ul className="fd-list">
                {b.drinks.map((v) => (
                  <li key={v.id} className="fd-row">
                    <span className="fd-row-text">
                      <strong>{v.drink_name}</strong>
                      <span className="muted small">
                        {t("fd.badge_no", { n: v.badge ?? 0 })} · {v.name}
                      </span>
                    </span>
                    <Button
                      size="sm"
                      variant="success"
                      icon="check"
                      busy={busy}
                      onClick={() =>
                        void act(
                          "frontdesk.serve_drink",
                          { id: v.id },
                          t("fd.served"),
                        )
                      }
                    >
                      {t("fd.serve")}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="panel fd-panel">
            <div className="panel-head">
              <h2>{t("fd.expected")}</h2>
              <span className="muted small">{b.expected.length}</span>
            </div>
            {!b.expected.length ? (
              <p className="muted">{t("fd.none_expected")}</p>
            ) : (
              <ul className="fd-list">
                {b.expected.map((v) => (
                  <li key={v.id} className="fd-row">
                    <span className="fd-row-text">
                      <strong>{who(v)}</strong>
                      <span className="muted small">
                        {[
                          clock(v.planned_at!),
                          v.host_name
                            ? t("fd.visiting", { name: v.host_name })
                            : null,
                          v.purpose,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    <span className="fd-row-end">
                      <Button
                        size="sm"
                        variant="primary"
                        icon="check"
                        busy={busy}
                        onClick={() =>
                          void act(
                            "frontdesk.desk_check_in",
                            { id: v.id },
                            t("fd.signed_in", { name: v.name }),
                          )
                        }
                      >
                        {t("fd.sign_in")}
                      </Button>
                      <IconButton
                        icon="edit"
                        label={t("fd.edit_visit")}
                        onClick={() => setPlan(v)}
                      />
                      <IconButton
                        icon="x"
                        label={t("fd.cancel_visit")}
                        onClick={() => setDrop(v)}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {b.left.length ? (
            <section className="panel fd-panel">
              <div className="panel-head">
                <h2>{t("fd.left")}</h2>
                <span className="muted small">{b.left.length}</span>
              </div>
              <ul className="fd-list fd-list-quiet">
                {b.left.map((v) => (
                  <li key={v.id} className="fd-row">
                    <span className="fd-row-text">
                      <strong>{who(v)}</strong>
                      <span className="muted small">
                        {v.state === "out" && v.checked_in_at
                          ? `${clock(v.checked_in_at)}–${clock(v.checked_out_at!)}`
                          : t("fd.state_cancelled")}
                      </span>
                    </span>
                    {stateBadge(v.state)}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </div>
      {plan ? (
        <PlanDialog
          visit={plan === "new" ? null : plan}
          desk
          onClose={() => setPlan(null)}
          onSaved={reload}
        />
      ) : null}
      {drop ? (
        <Confirm
          title={t("fd.cancel_title", { name: drop.name })}
          body={t("fd.cancel_body")}
          confirm={t("fd.cancel_visit")}
          danger
          onClose={() => setDrop(null)}
          onConfirm={async () => {
            if (
              await act(
                "frontdesk.cancel_visit",
                { id: drop.id },
                t("fd.cancelled"),
              )
            )
              setDrop(null);
          }}
        />
      ) : null}
      {closing && closeAt ? (
        <Confirm
          title={t("fd.sign_out_all_title")}
          body={tn(
            "fd.sign_out_all_body",
            b.inside.filter((v) => v.location === closeAt).length ||
              b.inside.length,
          )}
          confirm={t("fd.sign_out_all")}
          onClose={() => setClosing(false)}
          onConfirm={async () => {
            const at = location
              ? [location]
              : [...new Set(b.inside.map((v) => v.location))];
            const ok = await run(async () => {
              for (const l of at)
                await action("frontdesk.check_out_all", { location: l });
              await reload();
            }, t("fd.all_signed_out"));
            if (ok) setClosing(false);
          }}
        />
      ) : null}
    </>
  );
}

// ---- my visitors -------------------------------------------------------------------------

function MineTab() {
  const { data, reload } = useAction<{ waiting: Visit[]; list: Visit[] }>(
    "frontdesk.mine",
  );
  useLive(reload);
  const { busy, run } = useSave();
  const [plan, setPlan] = useState<Visit | "new" | null>(null);
  const [drop, setDrop] = useState<Visit | null>(null);
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  return (
    <>
      <div className="fd-toolbar">
        <p className="muted fd-lead">{t("fd.mine_lead")}</p>
        <span className="fd-spacer" />
        <Button variant="primary" icon="plus" onClick={() => setPlan("new")}>
          {t("fd.expect")}
        </Button>
      </div>
      <section className="panel fd-panel">
        {!data.list.length ? (
          <EmptyState
            icon="calendar"
            title={t("fd.mine_empty")}
            body={t("fd.mine_empty_body")}
            action={
              <Button icon="plus" onClick={() => setPlan("new")}>
                {t("fd.expect")}
              </Button>
            }
          />
        ) : (
          <ul className="fd-list">
            {data.list.map((v) => {
              const waiting = v.state === "in" && !v.host_seen_at;
              return (
                <li
                  key={v.id}
                  className={`fd-row${waiting ? " fd-waiting" : ""}`}
                >
                  <span className="fd-row-text">
                    <strong>{who(v)}</strong>
                    <span className="muted small">
                      {[
                        v.state === "planned"
                          ? dateTime(v.planned_at!)
                          : v.checked_in_at
                            ? t("fd.arrived_at", {
                                time: clock(v.checked_in_at),
                              })
                            : null,
                        v.location_name,
                        v.drink_name && v.state === "in"
                          ? t("fd.drink_asked", { drink: v.drink_name })
                          : null,
                        v.purpose,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  <span className="fd-row-end">
                    {waiting ? (
                      <Button
                        size="sm"
                        variant="primary"
                        icon="send"
                        busy={busy}
                        onClick={() =>
                          void run(
                            () => comingFor(v.id).then(reload),
                            t("fd.told_reception"),
                          )
                        }
                      >
                        {t("fd.on_my_way")}
                      </Button>
                    ) : v.state === "in" ? (
                      <StatusBadge
                        tone="ok"
                        icon="check"
                        label={t("fd.you_said_coming")}
                      />
                    ) : (
                      stateBadge(v.state)
                    )}
                    {v.state === "planned" ? (
                      <>
                        <IconButton
                          icon="edit"
                          label={t("fd.edit_visit")}
                          onClick={() => setPlan(v)}
                        />
                        <IconButton
                          icon="x"
                          label={t("fd.cancel_visit")}
                          onClick={() => setDrop(v)}
                        />
                      </>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      {plan ? (
        <PlanDialog
          visit={plan === "new" ? null : plan}
          onClose={() => setPlan(null)}
          onSaved={reload}
        />
      ) : null}
      {drop ? (
        <Confirm
          title={t("fd.cancel_title", { name: drop.name })}
          body={t("fd.cancel_body")}
          confirm={t("fd.cancel_visit")}
          danger
          onClose={() => setDrop(null)}
          onConfirm={async () => {
            if (
              await run(
                async () => (
                  await action("frontdesk.cancel_visit", { id: drop.id }),
                  await reload()
                ),
                t("fd.cancelled"),
              )
            )
              setDrop(null);
          }}
        />
      ) : null}
    </>
  );
}

const localInput = (at: Date) =>
  new Date(at.getTime() - at.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
function nextHalfHour() {
  const d = new Date(Date.now() + 30 * 60_000);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  return d;
}

function PlanDialog({
  visit,
  desk,
  onClose,
  onSaved,
}: {
  visit: Visit | null;
  desk?: boolean;
  onClose: () => void;
  onSaved: () => unknown;
}) {
  const places = usePlaces();
  const { me } = useSession();
  const { busy, run } = useSave();
  const [people, setPeople] = useState<Person[] | null>(null);
  useEffect(() => {
    if (desk)
      void action<Person[]>("frontdesk.people", {}).then(setPeople, () =>
        setPeople([]),
      );
  }, [desk]);
  const [f, setF] = useState({
    name: visit?.name ?? "",
    company: visit?.company ?? "",
    phone: visit?.phone ?? "",
    purpose: visit?.purpose ?? "",
    at: localInput(
      visit?.planned_at ? new Date(visit.planned_at) : nextHalfHour(),
    ),
    location: visit?.location ?? places.here?.id ?? "",
    host: visit?.host ?? me?.userId ?? "",
    note: visit?.note ?? "",
  });
  const [tried, setTried] = useState(false);
  const upd = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const nameError =
    tried && f.name.trim().length < 2 ? t("fd.name_needed") : null;
  const save = async () => {
    setTried(true);
    if (f.name.trim().length < 2 || !f.at || !f.location) return;
    const ok = await run(
      async () => {
        await action("frontdesk.plan_visit", {
          id: visit?.id ?? null,
          location: f.location,
          name: f.name,
          company: f.company || null,
          phone: f.phone || null,
          purpose: f.purpose || null,
          planned_at: new Date(f.at).toISOString(),
          host: desk ? f.host || null : undefined,
          note: f.note || null,
        });
        await onSaved();
      },
      visit ? t("fd.saved") : t("fd.expected_saved", { name: f.name.trim() }),
    );
    if (ok) onClose();
  };
  return (
    <Dialog
      title={visit ? t("fd.edit_visit") : t("fd.expect")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => void save()}
          >
            {visit ? t("common.save") : t("fd.expect_save")}
          </Button>
        </>
      }
    >
      <p className="muted small">{t("fd.expect_lead")}</p>
      <div className="form-grid">
        <Field label={t("fd.visitor_name")} required error={nameError}>
          <input
            className="input"
            value={f.name}
            maxLength={80}
            autoComplete="off"
            onChange={(e) => upd({ name: e.target.value })}
            data-autofocus
          />
        </Field>
        <Field label={t("fd.company")}>
          <input
            className="input"
            value={f.company}
            maxLength={80}
            onChange={(e) => upd({ company: e.target.value })}
          />
        </Field>
        <Field label={t("fd.when")} required>
          <input
            className="input"
            type="datetime-local"
            value={f.at}
            onChange={(e) => upd({ at: e.target.value })}
          />
        </Field>
        <Field label={t("fd.phone")} hint={t("fd.phone_hint")}>
          <input
            className="input"
            type="tel"
            inputMode="tel"
            value={f.phone}
            maxLength={20}
            onChange={(e) => upd({ phone: e.target.value })}
          />
        </Field>
        {places.multi ? (
          <Field label={t("fd.location")} required>
            <select
              className="input"
              value={f.location}
              onChange={(e) => upd({ location: e.target.value })}
            >
              {places.places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {desk && people ? (
          <Field label={t("fd.host")}>
            <select
              className="input"
              value={f.host}
              onChange={(e) => upd({ host: e.target.value })}
            >
              <option value="">{t("fd.no_host")}</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label={t("fd.purpose")}>
          <input
            className="input"
            value={f.purpose}
            maxLength={200}
            placeholder={t("fd.purpose_ph")}
            onChange={(e) => upd({ purpose: e.target.value })}
          />
        </Field>
        <Field label={t("fd.note_for_desk")} hint={t("fd.note_hint")}>
          <input
            className="input"
            value={f.note}
            maxLength={200}
            onChange={(e) => upd({ note: e.target.value })}
          />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- the register ------------------------------------------------------------------------

function RegisterTab() {
  const today = todayIso();
  const [from, setFrom] = useState(today.slice(0, 8) + "01");
  const [to, setTo] = useState(today);
  const { data } = useAction<{ visits: (Visit & { wiped: boolean })[] }>(
    "frontdesk.register",
    { from, to },
  );
  const rows = data?.visits ?? [];
  return (
    <section className="panel fd-panel">
      <div className="fd-toolbar">
        <label className="fd-date">
          <span className="small muted">{t("fd.from")}</span>
          <input
            className="input"
            type="date"
            value={from}
            max={to}
            onChange={(e) => e.target.value && setFrom(e.target.value)}
          />
        </label>
        <label className="fd-date">
          <span className="small muted">{t("fd.to")}</span>
          <input
            className="input"
            type="date"
            value={to}
            min={from}
            onChange={(e) => e.target.value && setTo(e.target.value)}
          />
        </label>
        <span className="fd-spacer" />
        <Button
          icon="download"
          disabled={!rows.length}
          onClick={() =>
            downloadCsv(
              `visitors-${from}-${to}`,
              [
                t("fd.col_in"),
                t("fd.col_out"),
                t("fd.visitor_name"),
                t("fd.company"),
                t("fd.host"),
                t("fd.badge"),
                t("fd.location"),
              ],
              rows.map((v) => [
                dateTime(v.checked_in_at!),
                v.checked_out_at ? dateTime(v.checked_out_at) : "",
                v.wiped ? t("fd.wiped") : v.name,
                v.company ?? "",
                v.host_name ?? "",
                v.badge ?? "",
                v.location_name,
              ]),
            )
          }
        >
          {t("fd.download")}
        </Button>
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : (
        <Table
          head={[
            t("fd.col_in"),
            t("fd.col_out"),
            t("fd.visitor_name"),
            t("fd.company"),
            t("fd.host"),
            t("fd.badge"),
          ]}
          phoneHide={[1, 3, 5]}
          empty={t("fd.register_empty")}
          rows={rows.map((v) => ({
            key: v.id,
            cells: [
              dateTime(v.checked_in_at!),
              v.checked_out_at ? clock(v.checked_out_at) : "—",
              v.wiped ? <span className="muted">{t("fd.wiped")}</span> : v.name,
              v.company ?? "—",
              v.host_name ?? "—",
              v.badge ?? "—",
            ],
          }))}
        />
      )}
      <p className="muted small fd-foot">{t("fd.register_note")}</p>
    </section>
  );
}

// ---- set-up ------------------------------------------------------------------------------

function SetupTab() {
  const { data, reload } = useAction<Setup>("frontdesk.setup");
  const { busy, run } = useSave();
  const [station, setStation] = useState<Station | "new" | null>(null);
  const [drinks, setDrinks] = useState<Drink[] | null>(null);
  const [keep, setKeep] = useState("");
  useEffect(() => {
    if (data) {
      setDrinks(data.drinks.filter((d) => d.active));
      setKeep(String(data.keep_days));
    }
  }, [data]);
  if (!data || !drinks) return <Skeleton h={300} r="var(--radius-lg)" />;
  const keepN = Number(keep);
  return (
    <div className="fd-setup">
      <section className="panel fd-panel">
        <div className="panel-head">
          <div>
            <h2>{t("fd.stations")}</h2>
            <p className="muted small">{t("fd.stations_lead")}</p>
          </div>
          <Button icon="plus" onClick={() => setStation("new")}>
            {t("fd.add_station")}
          </Button>
        </div>
        {!data.stations.length ? (
          <EmptyState
            icon="contact"
            title={t("fd.no_stations")}
            body={t("fd.no_stations_body")}
          />
        ) : (
          <ul className="fd-list">
            {data.stations.map((s) => (
              <li key={s.id} className="fd-row">
                <span className="fd-row-text">
                  <strong>{s.name}</strong>
                  <span className="muted small">
                    {[
                      s.location_name,
                      s.ask_company ? t("fd.asks_company") : null,
                      s.ask_phone ? t("fd.asks_phone") : null,
                      s.offer_drinks ? t("fd.offers_drinks") : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className="fd-row-end">
                  {s.active ? (
                    <Link
                      className="btn btn-primary btn-sm"
                      to={`/frontdesk/kiosk/${s.id}`}
                    >
                      <Icon name="external" size={16} />
                      <span className="btn-label">{t("fd.open_kiosk")}</span>
                    </Link>
                  ) : (
                    <StatusBadge
                      tone="neutral"
                      icon="pause"
                      label={t("fd.off")}
                    />
                  )}
                  <IconButton
                    icon="edit"
                    label={t("fd.edit_station")}
                    onClick={() => setStation(s)}
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="panel fd-panel">
        <div className="panel-head">
          <div>
            <h2>{t("fd.drinks_offered")}</h2>
            <p className="muted small">{t("fd.drinks_lead")}</p>
          </div>
        </div>
        <ul className="fd-drink-edit">
          {drinks.map((d, i) => (
            <li key={d.id || `n${i}`}>
              <input
                className="input"
                aria-label={t("fd.drink_name", { n: i + 1 })}
                value={d.name}
                maxLength={40}
                onChange={(e) =>
                  setDrinks(
                    drinks.map((x, j) =>
                      j === i ? { ...x, name: e.target.value } : x,
                    ),
                  )
                }
              />
              <IconButton
                icon="up"
                label={t("fd.move_up")}
                disabled={i === 0}
                onClick={() => setDrinks(swap(drinks, i, i - 1))}
              />
              <IconButton
                icon="trash"
                label={t("fd.remove_drink")}
                onClick={() => setDrinks(drinks.filter((_, j) => j !== i))}
              />
            </li>
          ))}
        </ul>
        <div className="fd-actions">
          <Button
            icon="plus"
            disabled={drinks.length >= 30}
            onClick={() =>
              setDrinks([...drinks, { id: "", name: "", active: true }])
            }
          >
            {t("fd.add_drink")}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() =>
              void run(async () => {
                await action("frontdesk.save_drinks", {
                  drinks: drinks
                    .filter((d) => d.name.trim())
                    .map((d) => ({
                      id: d.id || null,
                      name: d.name,
                      active: true,
                    })),
                });
                await reload();
              }, t("fd.drinks_saved"))
            }
          >
            {t("fd.save_drinks")}
          </Button>
        </div>
      </section>
      <section className="panel fd-panel">
        <div className="panel-head">
          <div>
            <h2>{t("fd.privacy")}</h2>
            <p className="muted small">{t("fd.privacy_lead")}</p>
          </div>
        </div>
        <div className="fd-actions">
          <Field
            label={t("fd.keep_days")}
            error={
              keep && !(keepN >= 1 && keepN <= 3650)
                ? t("fd.keep_days_range")
                : null
            }
          >
            <input
              className="input fd-keep"
              type="number"
              inputMode="numeric"
              min={1}
              max={3650}
              value={keep}
              onChange={(e) => setKeep(e.target.value)}
            />
          </Field>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={
              !(keepN >= 1 && keepN <= 3650) || keepN === data.keep_days
            }
            onClick={() =>
              void run(
                async () => (
                  await action("frontdesk.save_settings", { keep_days: keepN }),
                  await reload()
                ),
                t("fd.saved"),
              )
            }
          >
            {t("common.save")}
          </Button>
        </div>
      </section>
      {station ? (
        <StationDialog
          station={station === "new" ? null : station}
          onClose={() => setStation(null)}
          onSaved={reload}
        />
      ) : null}
    </div>
  );
}

function swap<T>(list: T[], a: number, b: number): T[] {
  const out = [...list];
  [out[a], out[b]] = [out[b]!, out[a]!];
  return out;
}

function StationDialog({
  station,
  onClose,
  onSaved,
}: {
  station: Station | null;
  onClose: () => void;
  onSaved: () => unknown;
}) {
  const places = usePlaces();
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: station?.name ?? t("fd.default_station"),
    location: station?.location ?? places.here?.id ?? "",
    active: station?.active ?? true,
    ask_company: station?.ask_company ?? true,
    ask_phone: station?.ask_phone ?? false,
    ask_host: station?.ask_host ?? true,
    offer_drinks: station?.offer_drinks ?? true,
    welcome: station?.welcome ?? "",
  });
  const upd = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const switches: [keyof typeof f, MessageKey][] = [
    ["ask_company", "fd.sw_company"],
    ["ask_phone", "fd.sw_phone"],
    ["ask_host", "fd.sw_host"],
    ["offer_drinks", "fd.sw_drinks"],
    ["active", "fd.sw_active"],
  ];
  return (
    <Dialog
      title={station ? t("fd.edit_station") : t("fd.add_station")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!f.name.trim() || !f.location}
            onClick={() =>
              void run(async () => {
                await action("frontdesk.save_station", {
                  id: station?.id ?? null,
                  ...f,
                  welcome: f.welcome || null,
                });
                await onSaved();
                onClose();
              }, t("fd.saved"))
            }
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t("fd.station_name")} required>
          <input
            className="input"
            value={f.name}
            maxLength={60}
            onChange={(e) => upd({ name: e.target.value })}
          />
        </Field>
        {places.multi ? (
          <Field label={t("fd.location")} required>
            <select
              className="input"
              value={f.location}
              onChange={(e) => upd({ location: e.target.value })}
            >
              {places.places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label={t("fd.welcome")} hint={t("fd.welcome_hint")}>
          <input
            className="input"
            value={f.welcome}
            maxLength={200}
            placeholder={t("fd.welcome_ph")}
            onChange={(e) => upd({ welcome: e.target.value })}
          />
        </Field>
      </div>
      <div className="fd-switches">
        {switches.map(([k, label]) => (
          <label key={k} className="switch-row">
            <span>{t(label)}</span>
            <Switch
              checked={!!f[k]}
              label={t(label)}
              onChange={(v) => upd({ [k]: v } as Partial<typeof f>)}
            />
          </label>
        ))}
      </div>
    </Dialog>
  );
}

// ---- the kiosk ---------------------------------------------------------------------------

interface KioskData {
  station: Station;
  location_name: string;
  hosts: Person[];
  drinks: Drink[];
  expected: { id: string; label: string; host_name: string | null }[];
}
type Step = "start" | "details" | "host" | "drink" | "done" | "leave" | "bye";

/** The tablet at the entrance: big buttons, one question at a time, back to the start on its own. */
export function FrontdeskKiosk() {
  const { station = "" } = useParams();
  const navigate = useNavigate();
  const { fail } = useUi();
  const [k, setK] = useState<KioskData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useMemo(
    () => () =>
      action<KioskData>("frontdesk.kiosk", { station }).then(
        (x) => (setK(x), setError(null)),
        (e: Error) => setError(e.message),
      ),
    [station],
  );
  useEffect(() => void load(), [load]);
  useLive(load);
  const blank = {
    planned: null as string | null,
    plannedLabel: "",
    name: "",
    company: "",
    phone: "",
    host: "",
    drink: "",
  };
  const [f, setF] = useState(blank);
  const [step, setStep] = useState<Step>("start");
  const [done, setDone] = useState<{
    badge: number;
    name: string;
    host_name: string | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [out, setOut] = useState({ badge: "", name: "" });
  const [outError, setOutError] = useState<string | null>(null);
  const restart = () => {
    setF(blank);
    setSearch("");
    setOut({ badge: "", name: "" });
    setOutError(null);
    setDone(null);
    setStep("start");
  };
  // Back to the start after a while without a touch, so the next visitor never sees someone else's details.
  useEffect(() => {
    if (step === "start") return;
    const idle = window.setTimeout(
      restart,
      step === "done" || step === "bye" ? 12_000 : 90_000,
    );
    return () => window.clearTimeout(idle);
  }, [step, f, search, out]);
  if (error)
    return (
      <div className="kiosk fd-kiosk">
        <div className="fd-k-card">
          <EmptyState
            icon="alert"
            title={error}
            action={
              <Button onClick={() => navigate("/frontdesk?tab=setup")}>
                {t("fd.back_to_setup")}
              </Button>
            }
          />
        </div>
      </div>
    );
  if (!k) return <div className="kiosk fd-kiosk" aria-busy="true" />;
  const s = k.station;
  const after = (from: Step): Step => {
    const order: Step[] = ["details", "host", "drink"];
    for (const x of order.slice(order.indexOf(from) + 1)) {
      if (x === "host" && s.ask_host && !f.planned) return x;
      if (x === "drink" && s.offer_drinks && k.drinks.length) return x;
    }
    return "done";
  };
  const finish = async (drink: string) => {
    setBusy(true);
    try {
      const r = await action<{
        badge: number;
        name: string;
        host_name: string | null;
      }>("frontdesk.check_in", {
        station: s.id,
        planned: f.planned,
        name: f.planned ? null : f.name,
        company: f.company || null,
        phone: f.phone || null,
        host: f.host || null,
        drink: drink || null,
      });
      setDone(r);
      setStep("done");
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const next = (from: Step, drink = f.drink) => {
    const to = after(from);
    if (to === "done") void finish(drink);
    else setStep(to);
  };
  const detailsOk =
    (f.planned || f.name.trim().length >= 2) &&
    (!s.ask_phone || f.planned || /^[+0-9 ()-]{6,20}$/.test(f.phone.trim()));
  const hosts = k.hosts.filter((h) =>
    h.name.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const leave = async () => {
    setBusy(true);
    setOutError(null);
    try {
      await action("frontdesk.kiosk_check_out", {
        station: s.id,
        badge: Number(out.badge),
        name: out.name,
      });
      setStep("bye");
    } catch (e) {
      setOutError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="kiosk fd-kiosk">
      <header className="fd-k-head">
        <span className="fd-k-place">
          <Icon name="contact" size={20} /> {k.location_name}
        </span>
        <IconButton
          icon="x"
          label={t("fd.close_kiosk")}
          onClick={() => navigate("/frontdesk?tab=setup")}
        />
      </header>
      <main className="fd-k-card" aria-live="polite">
        {step === "start" ? (
          <>
            <h1 className="fd-k-title">{t("fd.k_welcome")}</h1>
            <p className="fd-k-sub">{s.welcome || t("fd.k_welcome_sub")}</p>
            {k.expected.length ? (
              <div className="fd-k-expected">
                <h2>{t("fd.k_expected")}</h2>
                <div className="fd-k-chips">
                  {k.expected.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      className="fd-k-chip"
                      onClick={() => {
                        setF({
                          ...blank,
                          planned: e.id,
                          plannedLabel: e.label,
                        });
                        setStep("details");
                      }}
                    >
                      <strong>{e.label}</strong>
                      {e.host_name ? (
                        <span>{t("fd.k_for", { name: e.host_name })}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="fd-k-big">
              <Button
                variant="primary"
                size="xl"
                icon="contact"
                onClick={() => setStep("details")}
              >
                {t("fd.k_sign_in")}
              </Button>
              <Button size="xl" icon="logout" onClick={() => setStep("leave")}>
                {t("fd.k_leaving")}
              </Button>
            </div>
          </>
        ) : step === "details" ? (
          <>
            <h1 className="fd-k-title">
              {f.planned
                ? t("fd.k_hello", { name: f.plannedLabel })
                : t("fd.k_who")}
            </h1>
            <div className="fd-k-form">
              {f.planned ? null : (
                <Field label={t("fd.k_your_name")} required>
                  <input
                    className="input input-xl"
                    value={f.name}
                    maxLength={80}
                    autoComplete="off"
                    autoCapitalize="words"
                    onChange={(e) => setF({ ...f, name: e.target.value })}
                    data-autofocus
                  />
                </Field>
              )}
              {s.ask_company ? (
                <Field label={t("fd.k_company")}>
                  <input
                    className="input input-xl"
                    value={f.company}
                    maxLength={80}
                    autoComplete="off"
                    onChange={(e) => setF({ ...f, company: e.target.value })}
                  />
                </Field>
              ) : null}
              {s.ask_phone && !f.planned ? (
                <Field
                  label={t("fd.k_phone")}
                  required
                  hint={t("fd.phone_hint")}
                >
                  <input
                    className="input input-xl"
                    type="tel"
                    inputMode="tel"
                    value={f.phone}
                    maxLength={20}
                    autoComplete="off"
                    onChange={(e) => setF({ ...f, phone: e.target.value })}
                  />
                </Field>
              ) : null}
            </div>
            <KioskNav
              onBack={restart}
              next={t("fd.k_next")}
              disabled={!detailsOk || busy}
              busy={busy}
              onNext={() => next("details")}
            />
          </>
        ) : step === "host" ? (
          <>
            <h1 className="fd-k-title">{t("fd.k_whom")}</h1>
            <input
              className="input input-xl fd-k-search"
              type="search"
              placeholder={t("fd.k_search")}
              aria-label={t("fd.k_search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-autofocus
            />
            <div className="fd-k-hosts" role="list">
              {hosts.map((h) => (
                <button
                  key={h.id}
                  type="button"
                  role="listitem"
                  className={`fd-k-host${f.host === h.id ? " on" : ""}`}
                  aria-pressed={f.host === h.id}
                  onClick={() => {
                    setF({ ...f, host: h.id });
                    next("host");
                  }}
                >
                  <span className="fd-k-avatar" aria-hidden="true">
                    {h.name
                      .split(/\s+/)
                      .map((p) => p[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                  {h.name}
                </button>
              ))}
              {!hosts.length ? (
                <p className="muted">{t("fd.k_nobody")}</p>
              ) : null}
            </div>
            <KioskNav onBack={() => setStep("details")} />
          </>
        ) : step === "drink" ? (
          <>
            <h1 className="fd-k-title">{t("fd.k_drink")}</h1>
            <div className="fd-k-chips fd-k-drinks">
              {k.drinks.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className="fd-k-chip"
                  disabled={busy}
                  onClick={() => {
                    setF({ ...f, drink: d.id });
                    next("drink", d.id);
                  }}
                >
                  <strong>{d.name}</strong>
                </button>
              ))}
            </div>
            <KioskNav
              onBack={() =>
                setStep(s.ask_host && !f.planned ? "host" : "details")
              }
              next={t("fd.k_no_thanks")}
              busy={busy}
              onNext={() => next("drink", "")}
            />
          </>
        ) : step === "done" && done ? (
          <div className="fd-k-done">
            <span className="fd-k-check" aria-hidden="true">
              <Icon name="check" size={40} />
            </span>
            <h1 className="fd-k-title">
              {t("fd.k_thanks", { name: done.name.split(/\s+/)[0]! })}
            </h1>
            <p className="fd-k-sub">
              {done.host_name
                ? t("fd.k_told", { name: done.host_name })
                : t("fd.k_told_desk")}
            </p>
            <p className="fd-k-badge">
              <span>{t("fd.k_badge")}</span>
              <strong>{done.badge}</strong>
            </p>
            <Button size="xl" onClick={restart}>
              {t("fd.k_done")}
            </Button>
          </div>
        ) : step === "leave" ? (
          <>
            <h1 className="fd-k-title">{t("fd.k_leaving_title")}</h1>
            <p className="fd-k-sub">{t("fd.k_leaving_sub")}</p>
            <div className="fd-k-form">
              <Field label={t("fd.k_badge")} required>
                <input
                  className="input input-xl"
                  inputMode="numeric"
                  value={out.badge}
                  maxLength={6}
                  onChange={(e) =>
                    setOut({ ...out, badge: e.target.value.replace(/\D/g, "") })
                  }
                  data-autofocus
                />
              </Field>
              <Field label={t("fd.k_first_name")} required error={outError}>
                <input
                  className="input input-xl"
                  value={out.name}
                  maxLength={80}
                  autoComplete="off"
                  onChange={(e) => setOut({ ...out, name: e.target.value })}
                />
              </Field>
            </div>
            <KioskNav
              onBack={restart}
              next={t("fd.k_sign_out")}
              disabled={!out.badge || out.name.trim().length < 2}
              busy={busy}
              onNext={() => void leave()}
            />
          </>
        ) : (
          <div className="fd-k-done">
            <span className="fd-k-check" aria-hidden="true">
              <Icon name="check" size={40} />
            </span>
            <h1 className="fd-k-title">{t("fd.k_bye")}</h1>
            <p className="fd-k-sub">{t("fd.k_bye_sub")}</p>
            <Button size="xl" onClick={restart}>
              {t("fd.k_done")}
            </Button>
          </div>
        )}
      </main>
      <footer className="fd-k-foot muted small">{t("fd.k_privacy")}</footer>
    </div>
  );
}

function KioskNav({
  onBack,
  onNext,
  next,
  disabled,
  busy,
}: {
  onBack: () => void;
  onNext?: () => void;
  next?: string;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <div className="fd-k-nav">
      <Button size="xl" variant="ghost" icon="back" onClick={onBack}>
        {t("fd.k_back")}
      </Button>
      {onNext ? (
        <Button
          size="xl"
          variant="primary"
          disabled={disabled}
          busy={busy}
          onClick={onNext}
        >
          {next}
        </Button>
      ) : null}
    </div>
  );
}
